import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PackagesApi from 'legacyfrontend/api/paquetes';
import { dataDefaultValues } from 'legacyfrontend/constants';

export const MIN_AIRPORT_QUERY_LENGTH = 3;

export function useFlightAirportOptions({ showError } = {}) {
  const [flightOrigins, setFlightOrigins] = useState(dataDefaultValues);
  const [flightDestinations, setFlightDestinations] = useState(dataDefaultValues);
  const [searchOrigin, setSearchOrigin] = useState('');
  const [searchDestination, setSearchDestination] = useState('');
  const searchOriginRef = useRef('');
  const searchDestinationRef = useRef('');

  const [defaultOriginOptions, setDefaultOriginOptions] = useState([]);
  const [defaultDestOptions, setDefaultDestOptions] = useState([]);
  const [originSuggestionsLoaded, setOriginSuggestionsLoaded] = useState(false);
  const [destSuggestionsLoaded, setDestSuggestionsLoaded] = useState(false);

  const setOriginSearchValue = useCallback((value) => {
    const nextValue = value || '';
    searchOriginRef.current = nextValue;
    setSearchOrigin(nextValue);
  }, []);

  const setDestinationSearchValue = useCallback((value) => {
    const nextValue = value || '';
    searchDestinationRef.current = nextValue;
    setSearchDestination(nextValue);
  }, []);

  const trackOriginSearchValue = useCallback((value) => {
    searchOriginRef.current = value || '';
  }, []);

  const trackDestinationSearchValue = useCallback((value) => {
    searchDestinationRef.current = value || '';
  }, []);

  const fetchAirports = useCallback(
    async (value, isOrigin = true) => {
      const trimmed = typeof value === 'string' ? value.trim() : '';
      if (!trimmed || trimmed.includes('undefined') || trimmed.length < MIN_AIRPORT_QUERY_LENGTH) {
        if (isOrigin) {
          setFlightOrigins({ isLoading: false, error: false, data: [] });
        } else {
          setFlightDestinations({ isLoading: false, error: false, data: [] });
        }
        return;
      }

      try {
        const response = await PackagesApi.getAirports({ cityName: trimmed });
        const data = Array.isArray(response) ? response : [];
        if (isOrigin) {
          if (searchOriginRef.current.trim() !== trimmed) return;
          setFlightOrigins({ isLoading: false, error: false, data });
        } else {
          if (searchDestinationRef.current.trim() !== trimmed) return;
          setFlightDestinations({
            isLoading: false,
            error: false,
            data,
          });
        }
      } catch (error) {
        if (typeof showError === 'function') {
          showError(error?.message);
        }
        if (isOrigin) {
          setFlightOrigins({ data: [], error: true, isLoading: false });
        } else {
          setFlightDestinations({ data: [], error: true, isLoading: false });
        }
      }
    },
    [showError]
  );

  const loadDefaultSuggestions = useCallback(async () => {
    if (originSuggestionsLoaded && destSuggestionsLoaded) return;

    try {
      setFlightOrigins((prev) => ({ ...prev, isLoading: true }));
      setFlightDestinations((prev) => ({ ...prev, isLoading: true }));

      const response = await PackagesApi.getAirportSuggestions('all');
      const originData = Array.isArray(response?.origin) ? response.origin : [];
      const destinationData = Array.isArray(response?.destination) ? response.destination : [];

      setDefaultOriginOptions(originData);
      setDefaultDestOptions(destinationData);
      setOriginSuggestionsLoaded(true);
      setDestSuggestionsLoaded(true);
      if (!searchOriginRef.current.trim()) {
        setFlightOrigins({ isLoading: false, error: false, data: originData });
      }
      if (!searchDestinationRef.current.trim()) {
        setFlightDestinations({ isLoading: false, error: false, data: destinationData });
      }
    } catch {
      setDefaultOriginOptions([]);
      setDefaultDestOptions([]);
      setOriginSuggestionsLoaded(true);
      setDestSuggestionsLoaded(true);
      setFlightOrigins({ isLoading: false, error: false, data: [] });
      setFlightDestinations({ isLoading: false, error: false, data: [] });
    }
  }, [originSuggestionsLoaded, destSuggestionsLoaded]);

  const handleAirportChangeRef = useRef(null);

  const handleAirportChange = useCallback(
    async (_, value, isOrigin) => {
      const rawValue = typeof value === 'string' ? value : '';
      const trimmed = rawValue.trim();
      if (!trimmed || rawValue.includes('undefined')) {
        if (isOrigin) {
          setOriginSearchValue('');
          setFlightOrigins({ isLoading: false, error: false, data: [] });
        } else {
          setDestinationSearchValue('');
          setFlightDestinations({ isLoading: false, error: false, data: [] });
        }
        return;
      }

      if (trimmed.length < MIN_AIRPORT_QUERY_LENGTH) {
        if (isOrigin) {
          setOriginSearchValue(rawValue);
          setFlightOrigins({ isLoading: false, error: false, data: [] });
        } else {
          setDestinationSearchValue(rawValue);
          setFlightDestinations({ isLoading: false, error: false, data: [] });
        }
        return;
      }

      if (isOrigin) {
        setOriginSearchValue(rawValue);
        setFlightOrigins({ isLoading: true, error: false, data: [] });
      } else {
        setDestinationSearchValue(rawValue);
        setFlightDestinations({ isLoading: true, error: false, data: [] });
      }

      try {
        await fetchAirports(trimmed, isOrigin);
      } catch (error) {
        console.error('[fetchAirports] Error:', error.message);
      }
    },
    [fetchAirports, setOriginSearchValue, setDestinationSearchValue]
  );

  handleAirportChangeRef.current = handleAirportChange;

  const debouncedAirportChange = useMemo(
    () =>
      debounce((value, isOrigin) => {
        handleAirportChangeRef.current(null, value, isOrigin);
      }, 350),
    []
  );

  const handleSearchChange = useCallback(
    (value, isOrigin) => {
      debouncedAirportChange.cancel();

      const rawValue = typeof value === 'string' ? value : '';
      const trimmed = rawValue.trim();

      if (!value || typeof value !== 'string' || trimmed.length === 0 || rawValue.includes('undefined')) {
        if (isOrigin) {
          setOriginSearchValue('');
          setFlightOrigins({ isLoading: false, error: false, data: defaultOriginOptions });
        } else {
          setDestinationSearchValue('');
          setFlightDestinations({ isLoading: false, error: false, data: defaultDestOptions });
        }
        return;
      }

      if (trimmed.length < MIN_AIRPORT_QUERY_LENGTH) {
        if (isOrigin) {
          setOriginSearchValue(rawValue);
          setFlightOrigins({ isLoading: false, error: false, data: [] });
        } else {
          setDestinationSearchValue(rawValue);
          setFlightDestinations({ isLoading: false, error: false, data: [] });
        }
        return;
      }

      if (isOrigin) {
        setOriginSearchValue(rawValue);
        setFlightOrigins((prev) => ({ ...prev, isLoading: true }));
      } else {
        setDestinationSearchValue(rawValue);
        setFlightDestinations((prev) => ({ ...prev, isLoading: true }));
      }
      debouncedAirportChange(rawValue, isOrigin);
    },
    [debouncedAirportChange, defaultOriginOptions, defaultDestOptions, setOriginSearchValue, setDestinationSearchValue]
  );

  useEffect(() => () => debouncedAirportChange.cancel(), [debouncedAirportChange]);

  const suggestionsLoadedRef = useRef(false);
  const loadDefaultSuggestionsRef = useRef(loadDefaultSuggestions);
  useEffect(() => {
    loadDefaultSuggestionsRef.current = loadDefaultSuggestions;
  }, [loadDefaultSuggestions]);

  useEffect(() => {
    if (suggestionsLoadedRef.current) return;
    suggestionsLoadedRef.current = true;
    loadDefaultSuggestionsRef.current();
  }, []);

  return {
    flightOrigins,
    flightDestinations,
    searchOrigin,
    searchDestination,
    defaultOriginOptions,
    defaultDestOptions,
    originSuggestionsLoaded,
    destSuggestionsLoaded,
    handleSearchChange,
    setOriginSearchValue,
    setDestinationSearchValue,
    trackOriginSearchValue,
    trackDestinationSearchValue,
  };
}
